import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Floor "round-trip" mode: a warehouse tech who taps a Floor tile/card into a full
// DESK screen (Tag Parts, Rapid Photo, Carts, or a full record) is in round-trip mode.
// While active, the desk chrome is suppressed and a big "Done — Back to Floor" bar shows.
// Their server-side preferred_surface is NEVER touched — they stay a Floor user.
// The flag lives in sessionStorage so it survives in-page hops (capture → identify, etc.).
const KEY = "floor_roundtrip";
const Ctx = createContext(null);

export function clearFloorReturn() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

export function FloorReturnProvider({ children }) {
  const navigate = useNavigate();
  const [active, setActiveState] = useState(() => {
    try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setActive = useCallback((v) => {
    try { v ? sessionStorage.setItem(KEY, "1") : sessionStorage.removeItem(KEY); } catch { /* ignore */ }
    setActiveState(v);
    if (!v) setDirty(false);
  }, []);

  // The ONE way out of a Floor round-trip, shared by the bottom "Done" bar, the header
  // logo, and the header back button — so every "back toward Floor" behaves identically:
  // ends round-trip, returns to Floor Home, never touches preferred_surface.
  const doExit = useCallback(() => {
    setActive(false);
    setConfirmOpen(false);
    navigate("/floor");
  }, [setActive, navigate]);

  // Guarded exit — prompts only when there's genuinely unsaved work (dirty), else exits.
  const exitToFloor = useCallback(() => {
    if (dirty) setConfirmOpen(true);
    else doExit();
  }, [dirty, doExit]);

  return <Ctx.Provider value={{ active, setActive, dirty, setDirty, confirmOpen, setConfirmOpen, exitToFloor, doExit }}>{children}</Ctx.Provider>;
}

export function useFloorReturn() {
  return useContext(Ctx);
}

// Navigate from the Floor surface INTO a full desk screen, entering round-trip mode.
export function useGoDesk() {
  const nav = useNavigate();
  const fr = useFloorReturn();
  return useCallback((path) => {
    fr?.setActive(true);
    nav(path);
  }, [nav, fr]);
}
